import classNames from 'classnames';
import { map, get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import BackArrow, { DIRECTIONS } from 'components/BackArrow';
import Button, { TYPES } from 'components/Button';
import { Container, Grid, Cell } from 'components/Grid';
import { SecondaryHeading, TertiaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import RichText from 'components/RichText';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const BUTTON_SIZE = 340;
const MAX_HEIGHT = 626;
const MIN_HEIGHT = 400;

const FeaturedSlideShowDesktop = ({
  slides,
  heading,
  activeSlideIndex,
  handleOnChange,
  trackOnClick,
}) => {
  const shouldRenderNavArrows = slides.length !== 1;

  return (
    <Container>
      <div className="FeaturedSlideShowDesktop">
        <SecondaryHeading className="FeaturedSlideShowDesktop__heading">{heading}</SecondaryHeading>
        <div className="FeaturedSlideShowDesktop-wrapper">
          {map(slides, (slide, index) => (
            <div
              key={index}
              className={classNames('FeaturedSlideShowDesktop__slide', {
                'FeaturedSlideShowDesktop__slide--active': index === activeSlideIndex,
              })}
            >
              <Grid>
                <Cell largeUp={{ columns: 5 }}>
                  <div className="FeaturedSlideShowDesktop__image-wrapper">
                    <Image
                      className="FeaturedSlideShowDesktop__image"
                      src={get(slide, ['featuredImageLandscape', 'file', 'url'])}
                      alt={get(slide, ['featuredImageLandscape', 'description'])}
                      width={get(slide, [
                        'featuredImageLandscape',
                        'file',
                        'details',
                        'image',
                        'width',
                      ])}
                      height={get(slide, [
                        'featuredImageLandscape',
                        'file',
                        'details',
                        'image',
                        'height',
                      ])}
                    />
                  </div>
                </Cell>
                <Cell largeUp={{ columns: 7 }}>
                  <div className="FeaturedSlideShowDesktop__background-image">
                    <Image
                      className="FeaturedSlideShowDesktop__supporting-image"
                      src={get(slide, ['supportingDesktopImage', 'file', 'url'])}
                      alt={get(slide, ['supportingDesktopImage', 'description'])}
                      width={get(slide, [
                        'supportingDesktopImage',
                        'file',
                        'details',
                        'image',
                        'width',
                      ])}
                      height={get(slide, [
                        'supportingDesktopImage',
                        'file',
                        'details',
                        'image',
                        'height',
                      ])}
                      coverParentNode
                    />
                    <div className="FeaturedSlideShowDesktop__content-wrapper">
                      <div className="FeaturedSlideShowDesktop__text-container">
                        <TertiaryHeading className="FeaturedSlideShowDesktop__quote">
                          {get(slide, 'featuredHeading')}
                        </TertiaryHeading>
                        <RichText
                          className="FeaturedSlideShowDesktop__quote"
                          content={get(slide, 'featuredSubtext')}
                        />
                      </div>
                    </div>
                  </div>
                </Cell>
              </Grid>
            </div>
          ))}
          <Grid className="FeaturedSlideShowDesktop__overlay-grid">
            <Cell largeUp={{ columns: 5 }}>&nbsp;{/* layout placeholder */}</Cell>
            <Cell className="FeaturedSlideShowDesktop__overlay-content" largeUp={{ columns: 7 }}>
              {shouldRenderNavArrows && (
                <div className="FeaturedSlideShowDesktop__navigation">
                  <div className="FeaturedSlideShowDesktop__previous-arrow-wrapper">
                    <BackArrow
                      className="FeaturedSlideShowDesktop__previous-arrow"
                      disabled={activeSlideIndex === 0}
                      direction={DIRECTIONS.LEFT}
                      onClick={() => handleOnChange(activeSlideIndex - 1)}
                    >
                      {I18n.localize('landingPage.slideShow.previousSlide')}
                    </BackArrow>
                  </div>
                  <div className="FeaturedSlideShowDesktop__next-arrow-wrapper">
                    <BackArrow
                      className="FeaturedSlideShowDesktop__next-arrow"
                      direction={DIRECTIONS.RIGHT}
                      disabled={activeSlideIndex === slides.length - 1}
                      aria-disabled={activeSlideIndex === slides.length - 1}
                      onClick={() => handleOnChange(activeSlideIndex + 1)}
                    >
                      {I18n.localize('landingPage.slideShow.nextSlide')}
                    </BackArrow>
                  </div>
                </div>
              )}
              <div className="FeaturedSlideShowDesktop__cta-wrapper">
                <Button
                  className="FeaturedSlideShowDesktop__cta"
                  linkTo={get(slides, [activeSlideIndex, 'cta', 'url'])}
                  type={TYPES.INVERSE}
                  onClick={trackOnClick}
                >
                  {get(slides, [activeSlideIndex, 'cta', 'text'])}
                </Button>
              </div>
            </Cell>
          </Grid>
          <style jsx>
            {`
              .FeaturedSlideShowDesktop-wrapper {
                position: relative;
              }

              .FeaturedSlideShowDesktop__slide {
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                opacity: 0;
                transition: opacity 1s ease;
                pointer-events: none;
              }

              .FeaturedSlideShowDesktop__slide:first-child {
                position: relative;
              }

              .FeaturedSlideShowDesktop__slide--active {
                opacity: 1;
                pointer-events: auto;
              }

              .FeaturedSlideShowDesktop__background-image {
                position: relative;
                width: 100%;
                height: 100%;
                padding-left: ${Theme.get('spacing') * 4}px;
              }

              .FeaturedSlideShowDesktop__content-wrapper {
                position: absolute;
                top: 0;
                bottom: 0;
                display: flex;
                flex-direction: column;
                justify-content: center;
                text-align: center;
                width: 100%;
                padding: 0 ${Theme.get('spacing') * 20}px;
              }

              .FeaturedSlideShowDesktop__navigation {
                position: absolute;
                display: flex;
                align-items: center;
                height: 100%;
                right: 0;
                left: 0;
              }

              .FeaturedSlideShowDesktop__next-arrow-wrapper {
                position: absolute;
                display: flex;
                right: ${Theme.get('spacing') * 2}px;
              }

              .FeaturedSlideShowDesktop__previous-arrow-wrapper {
                position: absolute;
                display: flex;
                left: ${Theme.get('spacing') * 2}px;
              }

              .FeaturedSlideShowDesktop__cta-wrapper {
                display: flex;
                justify-content: center;
                padding-left: ${Theme.get('spacing') * 4}px;
              }
            `}
          </style>
          <style jsx global>
            {`
              .FeaturedSlideShowDesktop {
                padding: 0 ${Theme.get('spacing') * 8}px;
              }

              .FeaturedSlideShowDesktop__heading.typography--secondary-heading {
                padding-bottom: ${Theme.get('spacing') * 6}px;
              }

              .FeaturedSlideShowDesktop__quote.typography--tertiary-heading,
              .FeaturedSlideShowDesktop__quote.RichText .Paragraph {
                position: relative;
                color: ${Theme.get('colors-white')};
              }

              .FeaturedSlideShowDesktop__quote.typography--tertiary-heading {
                padding-bottom: ${Theme.get('spacing') * 3}px;
              }

              .FeaturedSlideShowDesktop__quote.RichText .Paragraph {
                margin-bottom: 0;
              }

              .FeaturedSlideShowDesktop__image.ImageV2 {
                height: 100%;
              }

              .FeaturedSlideShowDesktop__navigation .BackArrow {
                background: transparent;
                color: ${Theme.get('colors-white')};
              }

              .FeaturedSlideShowDesktop__navigation .BackArrow:before {
                border-color: ${Theme.get('colors-white')};
              }

              .FeaturedSlideShowDesktop__navigation .BackArrow:after {
                background-color: ${Theme.get('colors-white')};
              }

              .FeaturedSlideShowDesktop__previous-arrow.BackArrow {
                position: absolute;
                left: ${Theme.get('spacing') * 5}px;
              }

              .FeaturedSlideShowDesktop__next-arrow.BackArrow {
                position: absolute;
                right: 0;
              }

              .FeaturedSlideShowDesktop__cta.Button {
                position: absolute;
                width: ${BUTTON_SIZE}px;
                bottom: ${Theme.get('spacing') * 4}px;
                margin: auto;
                z-index: 1;
              }

              .FeaturedSlideShowDesktop__supporting-image.ImageV2 {
                height: ${MIN_HEIGHT}px;
                max-height: ${MAX_HEIGHT}px;
              }

              .FeaturedSlideShowDesktop__overlay-grid.Grid {
                position: absolute;
                top: 0;
                bottom: 0;
                display: flex;
                align-content: center;
                z-index: 0;
              }

              .FeaturedSlideShowDesktop__overlay-content.Grid__cell {
                height: 100%;
              }
            `}
          </style>
        </div>
      </div>
    </Container>
  );
};

FeaturedSlideShowDesktop.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  heading: PropTypes.string.isRequired,
  activeSlideIndex: PropTypes.number,
  handleOnChange: PropTypes.func,
  trackOnClick: PropTypes.func,
};

FeaturedSlideShowDesktop.defaultProps = {
  activeSlideIndex: 0,
  handleOnChange: () => {},
  trackOnClick: () => {},
};

export default FeaturedSlideShowDesktop;
